// src/redux/rootReducer.ts
import { combineReducers } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "./persistStorage";

// ----- Existing slices -----
import mainReducer from "./main/mainSlice";
import moderationReducer from "./moderation/moderationSlice";
import authReducer from "./auth/authSlice";

import profileReducer from "./profiles/profileSlice";
import profileBootstrapReducer from "./profiles/profileBootstrapSlice";
import profileMigrationReducer from "./profiles/profileMigrationSlice";

import identityReducer from "./identity/identitySlice";
import townlitVerificationReducer from "./identity/townlitVerificationSlice";

import giftReducer from "./profiles/giftSlice";
import friendshipReducer from "./network/friendshipSlice";
import fellowshipReducer from "./network/fellowshipSlice";
import socialLinksReducer from "./network/socialLinksSlice";
import conversationReducer from "./conversation/conversationSlice"; 
import presenceReducer from "./presence/presenceSlice";
import deviceKeyReducer from "./crypto/deviceKeySlice";
import paymentReducer from "./payment/paymentSlice";
import litshieldReducer from "./litshield/litshieldSlice";
import memberServicesReducer from "./profiles/servicesSlice";

import reactionsReducer from "./ugc/reactionsSlice";
import commentsReducer from "./ugc/commentsSlice";
import interactionsReducer from "./ugc/interactionSlice"; 

import testimoniesReducer from "./ugc/testimoniesSlice";
import momentsReducer from "./ugc/momentSlice";
import prayersReducer from "./ugc/prayerSlice";

import notificationsReducer from "./notifications/notificationsSlice";
import sanctuaryReducer from "./sanctuary/sanctuarySlice";
import visitorReducer from "./visitor/visitorSlice";
import squareReducer from "./square/squareSlice";
import streamReducer from "./streams/streamSlice";

import helpSupportReducer from "./helpSupport/helpSupportSlice";

// ----- Persist configs -----
const authPersistConfig = {
  key: "auth",
  storage,
  whitelist: ["token", "user", "isLoggedIn", "hasCompletedSignup"],
};
const profilePersistConfig = { key: "profile", storage };
const conversationPersistConfig = {
  key: "conversation",
  storage,
  whitelist: ["dialogues"],
};

// ----- Persisted reducers -----
const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedProfileReducer = persistReducer(
  profilePersistConfig,
  profileReducer,
);
const persistedConversationReducer = persistReducer(
  conversationPersistConfig,
  conversationReducer,
);

// ----- Combine all slices -----
const appReducer = combineReducers({
  main: mainReducer,
  moderation: moderationReducer,
  auth: persistedAuthReducer,
  profile: persistedProfileReducer,
  profileBootstrap: profileBootstrapReducer,
  profileMigration: profileMigrationReducer,

  identity: identityReducer,
  townlitVerification: townlitVerificationReducer,

  conversation: persistedConversationReducer,
  gift: giftReducer,
  friendship: friendshipReducer,
  fellowship: fellowshipReducer,
  socialLinks: socialLinksReducer,
  presence: presenceReducer,
  deviceKeys: deviceKeyReducer,
  payment: paymentReducer,
  litshield: litshieldReducer,
  memberServices: memberServicesReducer,

  testimonies: testimoniesReducer,
  moments: momentsReducer,
  prayers: prayersReducer,

  reactions: reactionsReducer,
  comments: commentsReducer,
  interactions: interactionsReducer,

  notifications: notificationsReducer,
  sanctuary: sanctuaryReducer,

  visitor: visitorReducer,

  square: squareReducer,
  stream: streamReducer,

  helpSupport: helpSupportReducer,
});

// ----- Hard reset (on logout etc.) -----
const RESET_ACTION_TYPES = new Set([
  "auth/logoutUser/fulfilled",
  "auth/resetSession",
]);

const rootReducer = (state: any, action: any) => {
  if (RESET_ACTION_TYPES.has(action.type)) {
    state = undefined;
  }
  return appReducer(state, action);
};

export default rootReducer;
